// React
import { useContext, useEffect, useState } from "react"

// Components
import { BaseTable, Button, Card, ErrorText, HeaderMenu, Input } from "components"
import { Acc, Date, Deliver, Memo, PromisedDate, Supplier, SupplierId, TabSection, TableSection, TaxInclusive, TradingTerm } from "../../components"
import { Code } from "./Code"
import { Detail } from "./Detail"
import { Journal, Payment, Purchases } from "./displays"

// Contexts
import { DataJournalContext } from "contexts"
import { ProductContext } from "../../PurchaseOrder/components/DetailBody/contexts"
import { DataBillContext, DataContext, PaymentCountContext, ProbilContext, ProductUnitProvider } from "../contexts"

// Form
import { useFieldArray, useFormContext, useWatch } from "react-hook-form"
import { ErrorMessage } from "@hookform/error-message"

// Icons
import { TbPlus } from "react-icons/tb"

// Types
import type { DetailFormType, FormType } from "../utils"

// Utils
import { useProject, useTaxCode } from "utils"
import { duplicateChecker, tab_list } from "../utils"
import { useProduct } from "../../PurchaseOrder/components/DetailBody/utils"
import { LinkToPurchase } from "./LinkToPurchase"
import { useApprovalStore } from "../../utils"

const getSubtotalItem = (item: DetailFormType): number => {
  return item.total
}

export function InputSection(params: {
  actionsection?: JSX.Element
}): JSX.Element {
  // Hooks
  const bill = useContext(DataBillContext)
  const project = useProject()
  const tax_code = useTaxCode()
  const count = useContext(PaymentCountContext)
  const { data, isLoading, refetch } = useProduct()
  const { isEdit, isView } = useApprovalStore()
 
  

  // Form
  const { control, formState, getValues } = useFormContext<FormType>()
  const vendor_id = useWatch({
    control,
    name: "vendor_id"
  })
  const { fields, append, remove } = useFieldArray({
    control,
    name: "probildetail"
  })

  //mencari total balance
  const subtotal: number = getValues("probildetail").reduce((acc: number, item: DetailFormType): number => {
    return acc + getSubtotalItem(item)
  }, 0)
  const tax: number = getValues("probildetail").reduce((acc: number, item: DetailFormType): number => {
    // Vars
      const tax_item = tax_code.data.find(i => String(i.value) === String(item.tax_id))
      return acc + (getSubtotalItem(item) * (tax_item?.rate ?? 0) / 100)
  }, 0)
  const total = subtotal + Number(getValues("freight")) + tax
  const totalBalance = Number(total - Number(bill.total_payment))
  //end mencari total balance


  useEffect(() => {
    if (vendor_id) {
      refetch(vendor_id)
    }

    // eslint-disable-next-line
  }, [vendor_id])
  
  return (
    <section className="container my-5 flex flex-col gap-2">
      <HeaderMenu 
        title="DELIVERY BILL"
        action={isEdit ? "edit" : (isView ? "view" : "create")}
        >
        <Code value={getValues("trx_code")} />
      </HeaderMenu>

      <section className="grid lg:grid-cols-3 gap-x-6 gap-y-3 items-end">
        <Supplier
          disabled
          name="vendor_id"
          trading_term_name="payment_term"
          vendor_name="_vendor_name"
        />

        <TradingTerm name="payment_term" />
        <TaxInclusive name="tax_inclusive" />
      </section>

      <Card>
        <Card.Body className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
          <section className="flex flex-col gap-3">
            <Deliver
              deliver_name="location_id"
              detail_name="address"
              disabled={isView}
            />

            <Memo name="memo" disabled={isView} />
            <LinkSection />
          </section>

          <section className="flex flex-col gap-3">
            <Date name="transaction_date" disabled={isView} />
            <SupplierId name="referensi" disabled={isView} />

            <PromisedDate
              disabled
              name="promise_date"
            />

            <Delivery />

            <section className="flex lg:justify-end">
              <Button className="w-fit">
                <TbPlus /> ADD REMINDER
              </Button>
            </section>
          </section>
        </Card.Body>
      </Card>

      <DataJournalContext.Provider value={{ project, tax_code }}>
        <ProductContext.Provider value={{ data, isLoading }}>
          <ProductUnitProvider>
            <BaseTable>
              <TableHeader />

              <tbody>
                {fields.map((_, key) => (
                  <Detail
                    key={key}
                    index={key}
                    remove={remove}
                  />
                ))}
              </tbody>
            </BaseTable>
          </ProductUnitProvider>

          <ErrorMessage
            name="probildetail"
            errors={formState.errors}
            render={({ message }) => <ErrorText text={message} />}
          />

          <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
            <section className="flex flex-col gap-3">
              <PaymentStatus totalBalance={totalBalance}/>
            </section>

            <AccSection />
          </section>

          {!count.isLoading && (
            <ProbilContext.Provider value={{
              append: (value: DetailFormType) => {
                // Vars
                const data = duplicateChecker(value, getValues("probildetail"), Boolean(count.data))

                if (data) {
                  append(value)
                }
              }
            }}>
              <Tab />
            </ProbilContext.Provider>
          )}
        </ProductContext.Provider>
      </DataJournalContext.Provider>

      {params.actionsection}
    </section>
  )
}

function AccSection(): JSX.Element {
  // Hooks
  const data = useContext(DataBillContext)

  return (
    <Acc
      payment={data.total_payment}
      name={{
        detail: "probildetail",
        freight: "freight"
      }}
    />
  )
}

function Delivery(): JSX.Element {
  // Form
  const { getValues } = useFormContext<FormType>()

  return (
    <Input
      disabled
      label="DELIVERY"
      value={`#${getValues("delivery_no")}`}
    />
  )
}

function LinkSection() {
  // Hooks
  const data = useContext(DataContext)
  const { po_code } = useContext(DataBillContext)

  return (
    <LinkToPurchase
      purchase_order_id={data.purchase_order_id.toString()}
      trx_code={po_code ?? data.trx_code}
    />
  )
}

function PaymentStatus(params:{
  totalBalance:number
}): JSX.Element {
  // Hooks
  const dataBill = useContext(DataBillContext)
  let billStatus = "Open"
  if(params.totalBalance === 0){
    billStatus = "Closed"
  }
  if(params.totalBalance < 0){
    billStatus = "Credit"
  }
  return (
    <TableSection>

      <TableSection.Row
        label="BILL STATUS"
        value={billStatus}
      />

      <TableSection.Row
        label="APPROVAL STATUS"
        value={dataBill.approval_status}
      />



      <TableSection.Row
        label="APPROVED BY"
        value={dataBill.approved_by}
      />
    </TableSection>
  )
}

function Tab(): JSX.Element {
  // Hooks
  const data = useContext(DataContext)
  const [tab, setTab] = useState<string[]>([])
  const [content, setContent] = useState<(() => JSX.Element)[]>([])
  const [currentTab, setCurrentTab] = useState<number>(0)

  // Form
  const { formState } = useFormContext<FormType>()

  useEffect(() => {
    // Vars
    const tab: string[] = [...tab_list]

    if (formState.defaultValues?.trx_code && data.approval_status !== 1) {
      tab.splice(0, 1)
      setTab(tab)
      setContent([Payment, Journal])
    } else {
      setContent([Purchases, Payment, Journal])
      setTab(tab)
    }

    return () => {
      setContent([])
      setTab([])
    }

    // eslint-disable-next-line
  }, [])

  return (
    <section>
      <TabSection
        activeIndex={currentTab}
        list={tab}
        onChangeTab={setCurrentTab}
      />

      {content.map((Element, key) => {
        if (currentTab === key) {
          return <Element key={key} />
        }

        return null
      })}
    </section>
  )
}

function TableHeader(): JSX.Element {
  // Hooks
  const data = useContext(DataContext)

  // Form
  const { formState } = useFormContext<FormType>()

  return (
    <thead className="!bg-black !text-white">
      <tr>
        {formState.defaultValues?.trx_code && data.approval_status !== 1 ? (
          <></>
        ) : (
          <th>BACKORDER</th>
        )}

        <th>DELIVERED</th>
        <th>ITEM CODE</th>
        <th>DESCRIPTION</th>
        <th>UNIT</th>
        <th>PRICE</th>
        <th>DISC (%)</th>
        <th>TOTAL</th>
        <th>JOB</th>
        <th>TAX</th>
        <th>WAREHOUSE</th>
        <th>ACTION</th>
      </tr>
    </thead>
  )
}